import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "theme-ui";
import { noop, get } from "sites-common/utils/lodash";
import HtmlDiv from "sites-common/components/HtmlDiv";
import DonationByCheque from "./DonationByCheque";
import DonationByNEFT from "./DonationByNEFT";
import FlexColumn from "../../FlexColumn";
import RadiosWithLabel from "../../RadiosWithLabel";
import { useFetchJson } from "../../../../fetch-url";

const getMandatoryEnv = require("sites-common/utils/getMandatoryEnv");

const { donationServiceConfig } = getMandatoryEnv(["donationServiceConfig"]);

const offlineSectionState = {
  donationByCheque: {
    chequeNo: "",
    chequeDate: null,
    chequeBankName: "",
  },
  donationByNEFT: {
    neftBankName: "",
    ifscCode: "",
    accountNo: "",
  },
};

export const useDonationOffline = (args = {}) => {
  const {
    showOfflineSection = false,
    country = "in",
    offlineSectionDetails = {
      ...offlineSectionState,
      payment_option: "Donation by Cheque",
    },
    onChange = noop,
    isOfflinePage = false,
  } = args;

  const onChangeOfflineSection = useCallback(
    (update) => {
      onChange({
        showOfflineSection,
        offlineSectionDetails,
        donation_type: "OFFLINE",
        ...update,
      });
    },
    [onChange, showOfflineSection, offlineSectionDetails]
  );
  const onChangeOfflineSectionDetails = React.useCallback(
    (update) => {
      onChangeOfflineSection({
        offlineSectionDetails: {
          ...offlineSectionDetails,
          ...update,
        },
      });
    },
    [offlineSectionDetails, onChangeOfflineSection]
  );
  const onChangeDonationByCheck = React.useCallback(
    (update, name) => {
      onChangeOfflineSectionDetails({
        donationByCheque: {
          ...offlineSectionDetails.donationByCheque,
          [name]: update,
        },
      });
    },
    [onChangeOfflineSectionDetails, offlineSectionDetails.donationByCheque]
  );
  const onChangeDonationByNEFT = React.useCallback(
    (update, name) => {
      onChangeOfflineSectionDetails({
        donationByNEFT: {
          ...offlineSectionDetails.donationByNEFT,
          [name]: update,
        },
      });
    },
    [onChangeOfflineSectionDetails, offlineSectionDetails.donationByNEFT]
  );

  useEffect(() => {
    if (isOfflinePage && !showOfflineSection) {
      onChangeOfflineSection({ showOfflineSection: true });
    }
  }, [isOfflinePage, onChangeOfflineSection, showOfflineSection]);

  // const offlineSectionSwitch = React.useMemo(
  //   () => ({
  //     defaultChecked: url.includes("offline") || showOfflineSection,
  //     onChange: ({ target: { checked } }) =>
  //       onChangeOfflineSection({
  //         showOfflineSection: checked,
  //       }),
  //   }),
  //   [onChangeOfflineSection, showOfflineSection, url]
  // );

  const paymentOptions = React.useMemo(
    () => [
      {
        value: "Donation by NEFT/RTGS",
        label: "Donation by NEFT/RTGS",
      },
      {
        value: "Donation by Cheque",
        label: "Donation by Cheque",
      },
    ],
    []
  );
  const paymentOptionsOthers = React.useMemo(
    () => [
      {
        value: "Donation by Cheque",
        label: "Donation by Check/Money Order",
      },
      {
        value: "AUTOMATIC_RECURRING_DONATION",
        label: "Automatic Recurring Donation",
      },
      {
        value: "DONATION_BY_STOCK_TRANSFER",
        label: "Donation by Stock Transfer",
      },
    ],
    []
  );

  const setRadiosOptions = React.useMemo(() => {
    let optionData = [];
    switch (country) {
      case "in":
        optionData = paymentOptions;
        break;
      case "de":
        optionData = [...paymentOptionsOthers].filter(
          (v) => v.value !== "DONATION_BY_STOCK_TRANSFER"
        );
        break;
      default:
        optionData = paymentOptionsOthers;
        break;
    }
    return optionData;
  }, [country, paymentOptions, paymentOptionsOthers]);

  const paymentOptionRadios = React.useMemo(
    () => ({
      name: "payment_option",
      options: setRadiosOptions,
      value: offlineSectionDetails.payment_option,
      defaultValue: true,
      onChange: (payment_option) =>
        onChangeOfflineSectionDetails({
          payment_option,
          ...offlineSectionState,
        }),
    }),
    [onChangeOfflineSectionDetails, offlineSectionDetails, setRadiosOptions]
  );

  const donationByCheque = React.memo(
    () => ({
      ...offlineSectionDetails.donationByCheque,
      onChange: (chequeDetails, name) =>
        onChangeDonationByCheck(chequeDetails, name),
    }),
    [offlineSectionDetails.donationByCheque, onChangeDonationByCheck]
  );
  const donationByNEFT = React.memo(
    () => ({
      ...offlineSectionDetails.donationByNEFT,
      onChange: (chequeDetails, name) =>
        onChangeDonationByNEFT(chequeDetails, name),
    }),
    [offlineSectionDetails.donationByNEFT, onChangeDonationByNEFT]
  );

  return {
    paymentOptionRadios,
    donationByCheque,
    donationByNEFT,
    showDonationByCheque: React.useMemo(
      () => offlineSectionDetails.payment_option === paymentOptions[1].value,
      [offlineSectionDetails.payment_option, paymentOptions]
    ),
    showDonationByNEFT: React.useMemo(
      () => offlineSectionDetails.payment_option === paymentOptions[0].value,
      [offlineSectionDetails.payment_option, paymentOptions]
    ),
    showOfflineSection,
    offlineSectionDetails,
  };
};

const RenderDocuments = (id, paymentOptionRadios, offlineSectionDetails) => {
  const { staticConf: url } = donationServiceConfig;
  const { data } = useFetchJson(
    `offline-documents`,
    `${url}documents.json`,
    {}
  );
  const [...documents] = get(data, id) || [];

  return (
    <FlexColumn FlexColumn gap={3} sx={{ color: "text" }}>
      <RadiosWithLabel
        label="Payment Option"
        radioWrapper={Box}
        style={{ flexDirection: "column", fontSize: "13px" }}
        {...paymentOptionRadios}
      />
      {documents.length > 0 &&
        documents.map(
          (item) =>
            offlineSectionDetails.payment_option === item.type && (
              <Box sx={{ m: "0px !important", fontSize: "14px" }}>
                <b>{item.name}:</b>{" "}
                <a href={item.link} target="_blank" rel="noreferrer">
                  {item.linkName}
                </a>
                <HtmlDiv htmlString={item.desc} />
              </Box>
            )
        )}
    </FlexColumn>
  );
};

const DonationOfflineSection = ({
  showOfflineSection,
  paymentOptionRadios,
  showDonationByCheque,
  donationByCheque,
  donationByNEFT,
  showDonationByNEFT,
  country,
  donationId,
  offlineSectionDetails,
}) => (
  <FlexColumn gap={3} sx={{ color: "text", mt: 2 }}>
    {/* Removed donation option switch  */}
    {/* <Switch
      label="Offline"
      sx={{ color: "text" }}
      disabled={isReadOnly}
      {...offlineSectionSwitch}
    /> */}
    {showOfflineSection &&
      (country === "in" ? (
        <FlexColumn gap={3} sx={{ color: "text", m: "0px" }}>
          <RadiosWithLabel
            label="Payment Option"
            style={{ fontSize: "13px", flexDirection: "column" }}
            radioWrapper={Box}
            {...paymentOptionRadios}
          />
          {showDonationByCheque && <DonationByCheque {...donationByCheque} />}
          {showDonationByNEFT && <DonationByNEFT {...donationByNEFT} />}
        </FlexColumn>
      ) : (
        RenderDocuments(donationId, paymentOptionRadios, offlineSectionDetails)
      ))}
  </FlexColumn>
);

DonationOfflineSection.propTypes = {
  paymentOptionRadios: PropTypes.shape({}),
  showDonationByCheque: PropTypes.bool,
  donationByCheque: PropTypes.shape({}),
  donationByNEFT: PropTypes.shape({}),
  showDonationByNEFT: PropTypes.bool,
  showDonationByChequeUSA: PropTypes.bool,
  showAutomaticRecurringDonation: PropTypes.bool,
  showDonationByStockTransfer: PropTypes.bool,
  showOfflineSection: PropTypes.bool,
  country: PropTypes.string,
  donationId: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  offlineSectionDetails: PropTypes.shape({}),
  isReadOnly: PropTypes.bool,
};

DonationOfflineSection.defaultProps = {
  paymentOptionRadios: {},
  showDonationByCheque: false,
  donationByCheque: {},
  showDonationByNEFT: false,
  donationByNEFT: {},
  showDonationByChequeUSA: false,
  showAutomaticRecurringDonation: false,
  showDonationByStockTransfer: false,
  showOfflineSection: false,
  country: "in",
  offlineSectionDetails: {},
  isReadOnly: false,
};
export default React.memo(DonationOfflineSection);
